import { drawExtensionIcon } from '@/extensions/pixelIcons';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import {
  clockedConfig,
  clockIo,
  clockModeItem,
  clockNode,
  clockedBuild,
  watchClocks,
} from '../clocked';
import { z } from 'zod';
import { configureTile } from '../configuration';
import { extensionCatalog, type DigitilesExtension } from '../catalog';
import { watchActiveExtensionNodes, type ActiveExtensionNode } from '../activeNodes';
import { createFunctionRunner } from './runner';
import type { LogicNodeInstance } from '@/models/logic/LogicNodeInstance';
import type { TileDefinition } from '@/models/tiles/TileDefinition';
import { InputType } from '@/models/tiles/TileDefinition';
import { CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { colors } from '@/styles';

export const JAVASCRIPT_TYPE = 'extension:digitiles.javascript/function' as const;
const defaultCode = 'return !input;';
const codeSchema = z
  .string()
  .trim()
  .min(1, 'Enter a function body.')
  .max(4096, 'Use at most 4096 characters.');
const stateSchema = z.object({ code: codeSchema, ...clockedConfig });
const runtimeSchema = stateSchema.extend({
  enabled: z.boolean(),
  input: z.boolean(),
  result: z.boolean(),
});
type Runtime = z.infer<typeof runtimeSchema>;
const ioSides = {
  [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
  [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
  [Side.BOTTOM]: clockIo,
  [Side.LEFT]: { type: TileSideIOType.INPUT, forceConnection: true },
};
const tile: TileDefinition<z.infer<typeof stateSchema>> = {
  type: JAVASCRIPT_TYPE,
  name: 'JavaScript function',
  canRotate: true,
  stateSchema,
  createDefaultState: () => ({ code: defaultCode, clocked: false }),
  graphConfiguration: (state) => ({ code: state.code, clocked: state.clocked }),
  ioSides,
  buildAndConnectNodes: clockedBuild(JAVASCRIPT_TYPE, true, true),
  createContextMenuItems: ({ instance, chipEditorModel }) => [
    clockModeItem({ instance, chipEditorModel }),
    {
      label: 'Edit JavaScript function',
      action: async (takeInput) => {
        try {
          const code = await takeInput(
            InputType.MULTILINE,
            'Function body — input is boolean; return true or false',
            instance.state.code,
            true,
            (value) => {
              const result = codeSchema.safeParse(value);
              return {
                isValid: result.success,
                errorMessage: result.error?.issues[0]?.message,
              };
            },
          );
          configureTile(
            instance,
            chipEditorModel,
            { code: codeSchema.parse(code) },
            'Change JavaScript function',
          );
        } catch {
          /* Cancel keeps the existing function. */
        }
      },
    },
  ],
  graphics: {
    type: JAVASCRIPT_TYPE,
    draw(g, { meta, connections, context }) {
      defaultTileGraphics(g);
      connectionGraphics(
        g,
        colors.signalColor,
        colors.noSignalColor,
        meta,
        connections,
        ioSides,
        context,
      );
      drawExtensionIcon(g, 'script', meta.outputs.right ? 0x8bd6ff : 0x8192a4);
    },
  },
};
export const javascriptExtension: DigitilesExtension = {
  id: 'digitiles.javascript',
  name: 'JavaScript functions',
  version: '1.1.0',
  description:
    'Evaluate a boolean function on input changes or, using the tile menu, a rising bottom clock. Left is data; right is the asynchronous result. Errors leave output low.',
  capabilities: ['javascript'],
  tiles: [{ definition: tile, circuits: [CircuitType.ChipLogic] }],
  nodes: [
    {
      type: JAVASCRIPT_TYPE,
      stateSchema: runtimeSchema.transform((state) => ({
        ...state,
        enabled: false,
        input: false,
        result: false,
      })),
      configurationKeys: ['code', 'enabled', 'clocked'],
      createDefaultState: (): Runtime => ({
        code: defaultCode,
        clocked: false,
        enabled: false,
        input: false,
        result: false,
      }),
      needsInputSync: (input, state: Runtime) => input !== state.input,
      process(input, state: Runtime) {
        state.input = input;
        return state.enabled && state.result;
      },
    },
    clockNode(JAVASCRIPT_TYPE),
  ],
  activate(app, signal) {
    const runner = createFunctionRunner();
    signal.addEventListener('abort', () => runner.dispose(), { once: true });
    let tracked = new Map<
      LogicNodeInstance,
      { input?: boolean; code?: string; revision: number; target: ActiveExtensionNode }
    >();
    let subscriptions: (() => void)[] = [];
    const report = (message: string) => {
      if (!signal.aborted) extensionCatalog.setError('digitiles.javascript', message);
    };
    const schedule = (target: ActiveExtensionNode, clock = false) => {
      const state = target.node.state as Runtime;
      const record = tracked.get(target.node);
      if (
        !record ||
        (state.clocked && !clock) ||
        (!clock && record.input === state.input && record.code === state.code)
      )
        return;
      if (clock) state.input = target.graph.getNodeInput(target.node.id);
      record.input = state.input;
      record.code = state.code;
      const revision = ++record.revision;
      const accepted = runner.submit(target.node, state.code, state.input, (result) => {
        if (
          signal.aborted ||
          tracked.get(target.node) !== record ||
          revision !== record.revision ||
          target.simulation.graph.nodes.get(target.node.id) !== target.node
        )
          return;
        const output = 'output' in result ? result.output : false;
        if ('error' in result) report(result.error);
        if (state.result !== output) {
          state.result = output;
          target.simulation.triggerUpdate(target.node.id);
        }
      });
      if (!accepted) {
        state.result = false;
        target.simulation.triggerUpdate(target.node.id);
        report(
          'JavaScript queue is full (128 pending functions). Change the input or re-enable live I/O to retry.',
        );
      }
    };
    const unwatch = watchActiveExtensionNodes(app, JAVASCRIPT_TYPE, (targets) => {
      subscriptions.forEach((dispose) => dispose());
      subscriptions = [];
      const retained = new Set(targets.map((target) => target.node));
      for (const [node, record] of tracked)
        if (!retained.has(node)) {
          runner.cancel(node);
          Object.assign(node.state, { enabled: false, result: false });
          if (record.target.simulation.graph.nodes.get(node.id) === node)
            record.target.simulation.triggerUpdate(node.id);
        }
      tracked = new Map(
        targets.map((target) => [
          target.node,
          tracked.get(target.node) ?? { revision: 0, target },
        ]),
      );
      const groups = new Map<
        ActiveExtensionNode['simulation'],
        Map<NodeHandle, ActiveExtensionNode>
      >();
      for (const target of targets) {
        const state = target.node.state as Runtime;
        state.enabled = true;
        target.simulation.triggerUpdate(target.node.id);
        let nodes = groups.get(target.simulation);
        if (!nodes) groups.set(target.simulation, (nodes = new Map()));
        nodes.set(target.node.id, target);
        schedule(target);
      }
      for (const [simulation, nodes] of groups)
        subscriptions.push(
          simulation.subscribeProcessed((ids) => {
            for (const id of ids.size < nodes.size ? ids : nodes.keys()) {
              if (!ids.has(id)) continue;
              const target = nodes.get(id);
              if (target) schedule(target);
            }
          }),
        );
    });
    const stopClock = watchClocks(app, JAVASCRIPT_TYPE, (target) => {
      if (target.node.state.clocked) schedule(target, true);
    });
    return () => {
      stopClock();
      unwatch();
      runner.dispose();
    };
  },
};
